import type { PrototypeApiClient } from "./apiClient";
import type { Root } from "./prototypeTypes";
import type { MediaRuntimeRef, MountedPlayer } from "./mediaPlayerTypes";
import { playerSelectors } from "./mediaPlayerSelectors";
import { rememberActiveAudioState, syncArticleAudioButtonForMedia, syncArticleAudioButtonForPlayback } from "./mediaPlayerArticleAudioButton";
import { getRuntime } from "./runtimeAccessor";
import { syncHomeRecentPlaybackControls, type HomeRecentAudioPlaybackState } from './homeRecentPlaybackPresentation';
import { resolveAudioPlaybackVisualState, setOptimisticAudioPlayback, type AudioPlaybackVisualState } from './audioPlaybackOptimisticIntent';
import { enqueueLatestMediaProgress } from './mediaProgressExecutor';
import { PAUSE_ICON_INNER, PLAY_ICON_INNER } from '../../shared/playerIcons';

// This belongs to the media instance (not the scrubber UI): it keeps an
// asynchronous Safari currentTime assignment coherent until a subsequent
// media report confirms it or the bounded fallback expires.
const PENDING_SEEK_CONFIRM_TOLERANCE_SECONDS = 0.5;
const PENDING_SEEK_MAX_WAIT_MS = 1_500;
const pendingSeekStartedAt = new WeakMap<MountedPlayer, number>();

// Same icon geometry as the Home Recent card (homeRecentPlaybackPresentation).
// Size is owned by .article-row-play svg, mirroring .recent-resume svg.
const TOPIC_ROW_PLAY_ICON = `<svg class="chapter-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="currentColor">${PLAY_ICON_INNER}</svg>`;
const TOPIC_ROW_PAUSE_ICON = `<svg class="chapter-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="currentColor">${PAUSE_ICON_INNER}</svg>`;

/**
 * Topic row controls are a projection of the mounted/optimistic audio owner.
 * They intentionally do not own playback: this makes a same-row second tap a
 * normal pause/resume while token failures roll back to the unplayed speaker.
 */
function syncTopicChapterPlayControls(root: Root, playback: AudioPlaybackVisualState | null): void {
	root.querySelectorAll<HTMLButtonElement>('.article-row-play[data-proto-click], .topic-cta-play[data-proto-click]').forEach((button) => {
		if (button.disabled || button.closest('.article-item.locked')) return;
		const match = button.dataset.protoClick?.match(/playTopicChapter\(\s*(\d+)/);
		const chapterIndex = match ? Number(match[1]) : NaN;
		const active = Number.isInteger(chapterIndex) && playback?.topicId === getRuntime(root)?.activeCourseId && playback?.chapterIndex === chapterIndex;
		const playing = active && playback?.paused === false;
		button.innerHTML = playing ? TOPIC_ROW_PAUSE_ICON : TOPIC_ROW_PLAY_ICON;
		button.classList.toggle('is-playing', playing);
		button.setAttribute('aria-pressed', String(playing));
		const title = button.closest<HTMLElement>('.article-item')?.querySelector<HTMLElement>('.article-title')?.textContent?.trim()
			?? button.closest<HTMLElement>('.topic-cta')?.querySelector<HTMLElement>('.topic-cta-title')?.textContent?.trim();
		button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} Chapter ${chapterIndex}${title ? `: ${title}` : ''}`);
	});
}

/**
 * A saved cursor at or beyond this fraction of the chapter duration is a
 * finished chapter: every entry (Recent card, prev/next, swipe, auto-advance
 * target) restarts it from 0:00 instead of resuming the endpoint. Unified
 * product threshold 2026-09-04 — 98% listened counts as listened-through.
 */
export const MEDIA_COMPLETED_RATIO = 0.98;

export function syncAudioUi(root: Root, media: MountedPlayer) {
  if (media.kind !== "audio") return;
  // The player can be re-parented into the mobile dock while a preserved
  // article root (or a transient render clone) still has the same IDs. Media
  // UI must always address the mounted player, never document's first match.
  const player = media.root.matches('#webPlayer') ? media.root : root.querySelector<HTMLElement>('#webPlayer');
  const uiRoot = player ?? root;
  rememberActiveAudioState(root, {
    topicId: media.topicId,
    chapterIndex: media.chapterIndex,
    paused: media.element.paused,
  });
  const visual = resolveAudioPlaybackVisualState(root, {
    topicId: media.topicId,
    chapterIndex: media.chapterIndex,
    paused: media.element.paused,
    ended: media.element.ended,
  });
  const playIcon = uiRoot.querySelector<HTMLElement>(`#${playerSelectors.audio.playIcon}`);
  const miniPlayIcon = uiRoot.querySelector<HTMLElement>('#miniPlayIcon');
	const iconMarkup = visual.paused ? PLAY_ICON_INNER : PAUSE_ICON_INNER;
  if (playIcon) playIcon.innerHTML = iconMarkup;
  if (miniPlayIcon) miniPlayIcon.innerHTML = iconMarkup;
  // Drives the playlist equalizer bars; they run only while audio plays.
  player?.classList.toggle('is-audio-paused', visual.paused);
  const duration = playbackDurationSeconds(media);
  const current = playbackCurrentSeconds(media);
  const pct = duration > 0 ? Math.min(100, Math.max(0, (current / duration) * 100)) : 0;
  // The active range instance owns its own UI during a local drag and the
  // final asynchronous seek. Do not use a global flag: preserved or cloned
  // players can coexist briefly while the mobile dock re-parents the bar.
  const slider = uiRoot.querySelector<HTMLInputElement>(`#${playerSelectors.audio.progressSlider}`);
  const scrubbing = slider?.dataset.audioScrubOwner !== undefined;
  const progress = uiRoot.querySelector<HTMLElement>(`#${playerSelectors.audio.progress}`);
  if (progress && !scrubbing) progress.style.width = `${pct}%`;
	const ring = uiRoot.querySelector<HTMLElement>('#playerThumbProgress');
	if (ring && !scrubbing) paintAudioProgressRing(ring, pct, duration);
  if (slider && !scrubbing) {
    slider.value = String(pct);
    slider.setAttribute('aria-valuetext', `${formatTime(current)} of ${formatTime(duration)}`);
  }
  const time = uiRoot.querySelector<HTMLElement>(`#${playerSelectors.audio.time}`);
  if (time && !scrubbing) time.textContent = `${formatTime(current)} / ${formatTime(duration)}`;
  const speed = uiRoot.querySelector<HTMLElement>(`#${playerSelectors.audio.speedBtn}`);
  if (speed) speed.textContent = `${formatRate(media.element.playbackRate || 1)}x`;
	syncArticleAudioButtonForMedia(root, media, visual);
	// Recent is only a projection of this element. Every playback entry point
	// reaches this sync, so cards cannot drift into an independent state.
	syncHomeRecentPlaybackControls(root, homeRecentAudioPlaybackState(root, media, visual));
	syncTopicChapterPlayControls(root, visual);
}

/**
 * One media-derived projection for both live timeupdate paints and a Home
 * re-hydration. `playbackCurrentSeconds` intentionally preserves a pending
 * virtual seek until the element confirms it, so a stale element report
 * cannot overwrite the card ring during a return to Home.
 */
export function homeRecentAudioPlaybackState(root: Root, media: MountedPlayer, visual?: AudioPlaybackVisualState): HomeRecentAudioPlaybackState | null {
	if (media.kind !== 'audio') return null;
	const state = visual ?? resolveAudioPlaybackVisualState(root, {
		topicId: media.topicId,
		chapterIndex: media.chapterIndex,
		paused: media.element.paused,
		ended: media.element.ended,
	});
	const duration = playbackDurationSeconds(media);
	const current = playbackCurrentSeconds(media);
	const progressPercent = duration > 0 ? Math.min(100, Math.max(0, (current / duration) * 100)) : 0;
	// While a different Recent chapter is waiting for its token, `state` is the
	// user's newest optimistic target but `media` is still the old audible
	// source. Do not attach A's chapter id/progress to B: Home deliberately
	// prefers chapterId for identity, which would otherwise make B look idle
	// until its token replaces A.
	const sameTarget = state.topicId === media.topicId && state.chapterIndex === media.chapterIndex;
	if (!sameTarget) return state;
	const chapterId = media.topicId && typeof media.chapterIndex === 'number'
		? runtimeOf(root)?.chapterIdByIndex?.[media.topicId]?.[media.chapterIndex]
		: undefined;
	return { ...state, chapterId, progressPercent };
}

/** Shared audio progress presentation for playback updates and drag previews. */
export function paintAudioProgressRing(ring: HTMLElement | null, percent: number, duration: number): void {
	if (!ring) return;
	const value = Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;
	ring.style.setProperty('--audio-progress', `${value}%`);
}

export function presentOptimisticAudioUi(root: Root, state: AudioPlaybackVisualState, preferredPlayer?: HTMLElement): void {
	setOptimisticAudioPlayback(root, state);
	const player = preferredPlayer ?? root.querySelector<HTMLElement>('#webPlayer');
	const uiRoot = player ?? root;
	const iconMarkup = state.paused ? PLAY_ICON_INNER : PAUSE_ICON_INNER;
	const playIcon = uiRoot.querySelector<HTMLElement>(`#${playerSelectors.audio.playIcon}`);
	const miniPlayIcon = uiRoot.querySelector<HTMLElement>('#miniPlayIcon');
	if (playIcon) playIcon.innerHTML = iconMarkup;
	if (miniPlayIcon) miniPlayIcon.innerHTML = iconMarkup;
	player?.classList.toggle('is-audio-paused', state.paused);
	syncArticleAudioButtonForPlayback(root, state);
	syncHomeRecentPlaybackControls(root, state);
	syncTopicChapterPlayControls(root, state);
}

export function resetAudioPlaybackUi(root: Root, preferredPlayer?: HTMLElement): void {
	const player = preferredPlayer ?? root.querySelector<HTMLElement>('#webPlayer');
	const uiRoot = player ?? root;
	const iconMarkup = PLAY_ICON_INNER;
	const playIcon = uiRoot.querySelector<HTMLElement>(`#${playerSelectors.audio.playIcon}`);
	const miniPlayIcon = uiRoot.querySelector<HTMLElement>('#miniPlayIcon');
	if (playIcon) playIcon.innerHTML = iconMarkup;
	if (miniPlayIcon) miniPlayIcon.innerHTML = iconMarkup;
	player?.classList.add('is-audio-paused');
	const progress = uiRoot.querySelector<HTMLElement>(`#${playerSelectors.audio.progress}`);
	if (progress) progress.style.width = '0%';
	const slider = uiRoot.querySelector<HTMLInputElement>(`#${playerSelectors.audio.progressSlider}`);
	if (slider) {
		slider.value = '0';
		slider.setAttribute('aria-valuetext', '0:00 of 0:00');
		delete slider.dataset.audioScrubOwner;
	}
	const time = uiRoot.querySelector<HTMLElement>(`#${playerSelectors.audio.time}`);
	if (time) time.textContent = '0:00 / 0:00';
	paintAudioProgressRing(uiRoot.querySelector<HTMLElement>('#playerThumbProgress'), 0, 0);
	syncArticleAudioButtonForPlayback(root, null);
	syncHomeRecentPlaybackControls(root, null);
	syncTopicChapterPlayControls(root, null);
}

export function syncVideoUi(root: Root, media: MountedPlayer) {
  if (media.kind !== "video") return;
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
  const duration = playbackDurationSeconds(media);
  const current = playbackCurrentSeconds(media);
  const pct = duration > 0 ? Math.min(100, Math.max(0, (current / duration) * 100)) : 0;
  const progress = doc.getElementById(playerSelectors.video.progressFill) as HTMLElement | null;
  if (progress) progress.style.width = `${pct}%`;
  const time = doc.getElementById(playerSelectors.video.time) as HTMLElement | null;
  if (time) time.textContent = duration > 0 ? `${formatTime(current)} / ${formatTime(duration)}` : "0:00";
  const icon = doc.getElementById(playerSelectors.video.playToggleIcon);
  if (icon) {
    icon.innerHTML = media.element.paused
		? PLAY_ICON_INNER
		: PAUSE_ICON_INNER;
  }
}

/** Paints a scrub preview without mutating the media element or its virtual seek state. */
export function previewVideoSeekUi(root: Root, media: MountedPlayer, seconds: number): void {
  if (media.kind !== 'video') return;
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
  const duration = playbackDurationSeconds(media);
  const current = duration > 0 ? Math.min(duration, Math.max(0, seconds)) : 0;
  const pct = duration > 0 ? (current / duration) * 100 : 0;
  const progress = doc.getElementById(playerSelectors.video.progressFill) as HTMLElement | null;
  if (progress) progress.style.width = `${pct}%`;
  const time = doc.getElementById(playerSelectors.video.time) as HTMLElement | null;
  if (time) time.textContent = duration > 0 ? `${formatTime(current)} / ${formatTime(duration)}` : '0:00';
}

export function restoreAudioPosition(root: Root, media: MountedPlayer) {
  if (media.kind !== "audio" || media.resumeApplied) return;
  restoreMediaPosition(root, media);
}

type PendingVideoRestore = {
	sourceGeneration: number | undefined;
	operationGeneration: number | undefined;
	cancel: () => void;
};

const pendingVideoRestores = new WeakMap<MountedPlayer, PendingVideoRestore>();

export function restoreVideoPosition(root: Root, media: MountedPlayer) {
	if (media.kind !== "video" || media.resumeApplied) return;
	const existing = pendingVideoRestores.get(media);
	if (existing) {
		if (existing.sourceGeneration === media.sourceGeneration && existing.operationGeneration === media.videoSourceOperationGeneration) return;
		existing.cancel();
	}
	const key = media.topicId && typeof media.chapterIndex === 'number' ? `${media.topicId}:${media.chapterIndex}` : activeProgressKey(root);
	const seconds = key ? runtimeOf(root)?.mediaResumeSecondsByChapter?.[key] : undefined;
	if (typeof seconds !== 'number' || seconds <= 0) {
		media.resumeApplied = true;
		return;
	}
	const video = media.element as HTMLVideoElement;
	const sourceGeneration = media.sourceGeneration;
	const operationGeneration = media.videoSourceOperationGeneration;
	const stillCurrent = () => media.element === video
		&& media.sourceGeneration === sourceGeneration
		&& media.videoSourceOperationGeneration === operationGeneration
		&& media.root.contains(video);
	let pending: PendingVideoRestore;
	const finish = () => {
		video.removeEventListener('loadeddata', apply);
		video.removeEventListener('canplay', apply);
		video.removeEventListener('progress', apply);
		if (pendingVideoRestores.get(media) === pending) pendingVideoRestores.delete(media);
	};
	const apply = () => {
		if (!stillCurrent()) {
			finish();
			return;
		}
		// Never seek from fallback duration or during metadata-only state. iOS
		// can paint a black frame when currentTime is assigned at this point.
		const duration = video.duration;
		if (!Number.isFinite(duration) || duration <= 0) return;
		// A finished chapter restarts from 0:00 on every entry (chapter swipes
		// included). No seekable check needed: 0 is always a valid position.
		if (seconds >= duration * MEDIA_COMPLETED_RATIO) {
			if (key) {
				const runtime = runtimeOf(root);
				if (runtime) runtime.mediaResumeSecondsByChapter = { ...(runtime.mediaResumeSecondsByChapter ?? {}), [key]: 0 };
			}
			setPlaybackCurrentSeconds(media, 0);
			media.resumeApplied = true;
			finish();
			syncMediaUi(root, media);
			return;
		}
		const target = clampVideoResumeTarget(video, seconds, duration);
		if (target === null) return;
		setPlaybackCurrentSeconds(media, target);
		if (key) {
			const runtime = runtimeOf(root);
			if (runtime) runtime.mediaResumeSecondsByChapter = { ...(runtime.mediaResumeSecondsByChapter ?? {}), [key]: target };
		}
		media.resumeApplied = true;
		finish();
		syncMediaUi(root, media);
	};
	pending = { sourceGeneration, operationGeneration, cancel: finish };
	pendingVideoRestores.set(media, pending);
	video.addEventListener('loadeddata', apply);
	video.addEventListener('canplay', apply);
	video.addEventListener('progress', apply);
	if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) apply();
}

function clampVideoResumeTarget(video: HTMLVideoElement, seconds: number, duration: number): number | null {
	const target = Math.min(duration, Math.max(0, seconds));
	if (video.seekable.length === 0) return null;
	for (let index = 0; index < video.seekable.length; index += 1) {
		const start = video.seekable.start(index);
		const end = video.seekable.end(index);
		if (target >= start && target <= end) return target;
	}
	// Progressive media can expose only [0, 1] while the requested 30 second
	// resume point is still buffering. Seeking to that transient edge produces
	// the reported black replay; wait for a later loadeddata/canplay instead.
	return null;
}

export function persistAudioProgress(root: Root, media: MountedPlayer, force = false) {
  if (media.kind !== "audio") return;
  persistMediaProgress(root, media, force);
}

export function persistVideoProgress(root: Root, media: MountedPlayer, force = false) {
  if (media.kind !== "video") return;
  persistMediaProgress(root, media, force);
}

export function syncMediaUi(root: Root, media: MountedPlayer) {
  if (media.kind === "audio") syncAudioUi(root, media);
  else syncVideoUi(root, media);
}

export function persistMediaProgress(root: Root, media: MountedPlayer, force = false) {
  const rt = runtimeOf(root);
  const topicId = media.topicId ?? rt?.activeCourseId;
  const chapterIndex = media.chapterIndex ?? rt?.activeChapterIndex;
  if (!rt?.api || !topicId || typeof chapterIndex !== "number") return;
  const duration = playbackDurationSeconds(media);
  if (duration <= 0) return;
	// Freeze the whole report before it enters the serial executor. A handoff
	// can mutate this same media element to N+1 while a chapter lookup awaits.
	const positionSeconds = Math.min(duration, Math.max(0, playbackCurrentSeconds(media)));
  const pct = Math.min(100, Math.max(1, Math.round((positionSeconds / duration) * 100)));
  const now = Date.now();
  if (!force && media.lastProgressSyncAt && now - media.lastProgressSyncAt < 10_000 && Math.abs(pct - (media.lastProgressPercent ?? 0)) < 5) return;
  media.lastProgressSyncAt = now;
  media.lastProgressPercent = pct;
	const key = `${topicId}:${chapterIndex}`;
  rt.resumeProgressByChapter = { ...(rt.resumeProgressByChapter ?? {}), [key]: pct };
	rt.mediaResumeSecondsByChapter = { ...(rt.mediaResumeSecondsByChapter ?? {}), [key]: positionSeconds };
	const report = {
		topicId,
		chapterIndex,
		kind: media.kind,
		positionSeconds,
		durationSeconds: duration,
		progressPercent: pct,
		status: pct >= 100 || media.element.ended ? 'completed' as const : 'in_progress' as const,
	};
	// Serialise before resolving the chapter id: two cache misses must not let
	// the first lookup finish last and overwrite the newer cursor.
	enqueueLatestMediaProgress(rt as never, `chapter-index:${key}`, {
      topicId: report.topicId,
			status: report.status,
			progressPercent: report.progressPercent,
			media: { kind: report.kind, positionSeconds: report.positionSeconds, durationSeconds: report.durationSeconds },
		}, () => resolveChapterId(rt, topicId, chapterIndex));
	// The write is what makes the server's Recent row reflect this chapter; the
	// rail read must follow the write, not precede it (an advance- or
	// handoff-time reconcile can otherwise land before the first write).
	void import('./homeRecentHydrator').then((hydrator) => {
		// The write this schedules after is already enqueued, so the read only
		// needs to outrun the write's own round-trip — not the ended-race margin
		// the default 2s delay exists for.
		if (rt.api) hydrator.scheduleHomeRecentReconcile(root, rt.api, 1200);
	}).catch(() => undefined);
}

export function runtimeOf(root: Root): MediaRuntimeRef | null {
  return getRuntime(root) ?? null;
}

export function activeAudioDurationFallback(root: Root): number | undefined {
  const key = activeProgressKey(root);
  const seconds = key ? runtimeOf(root)?.audioSecondsByChapter?.[key] : undefined;
  return typeof seconds === "number" && seconds > 0 ? seconds : undefined;
}

export function activeVideoDurationFallback(root: Root): number | undefined {
  const key = activeProgressKey(root);
  const seconds = key ? runtimeOf(root)?.videoSecondsByChapter?.[key] : undefined;
  return typeof seconds === "number" && seconds > 0 ? seconds : undefined;
}

export function playbackDurationSeconds(media: MountedPlayer): number {
  const duration = Number.isFinite(media.element.duration) ? media.element.duration : 0;
  return duration > 0 ? duration : media.fallbackDurationSeconds ?? 0;
}

export function playbackCurrentSeconds(media: MountedPlayer): number {
  const current = Number.isFinite(media.element.currentTime) ? media.element.currentTime : 0;
  // A seek assignment is asynchronous on Safari. Until the element confirms
  // the target, its old currentTime must not win a UI sync and pull the
  // scrubber back from where the user just released it. `virtualCurrentTime`
  // is therefore a pending seek target, not merely a metadata fallback.
  const pending = media.virtualCurrentTime;
  if (typeof pending === "number") {
    // `timeupdate` is intentionally coarse. A first report can already be
    // slightly beyond the target while audio is playing, which is a valid
    // confirmation rather than a reason to freeze the UI at the target.
    let startedAt = pendingSeekStartedAt.get(media);
    if (startedAt === undefined) {
      startedAt = Date.now();
      pendingSeekStartedAt.set(media, startedAt);
    }
    const confirmed = Math.abs(current - pending) <= PENDING_SEEK_CONFIRM_TOLERANCE_SECONDS;
    const timedOut = Date.now() - startedAt >= PENDING_SEEK_MAX_WAIT_MS;
    if (!confirmed && !timedOut) return pending;
    media.virtualCurrentTime = undefined;
    pendingSeekStartedAt.delete(media);
  }
  return current > 0 ? current : media.virtualCurrentTime ?? 0;
}

export function setPlaybackCurrentSeconds(media: MountedPlayer, seconds: number): void {
  const next = Math.max(0, seconds);
  media.virtualCurrentTime = next;
  pendingSeekStartedAt.set(media, Date.now());
  try {
    media.element.currentTime = next;
  } catch {
    // Keep the virtual value when metadata blocks currentTime.
  }
}

function activeProgressKey(root: Root): string | null {
  const rt = runtimeOf(root);
  if (!rt?.activeCourseId || typeof rt.activeChapterIndex !== "number") return null;
  return `${rt.activeCourseId}:${rt.activeChapterIndex}`;
}

function restoreMediaPosition(root: Root, media: MountedPlayer) {
  const key = media.topicId && typeof media.chapterIndex === "number" ? `${media.topicId}:${media.chapterIndex}` : activeProgressKey(root);
	const seconds = key ? runtimeOf(root)?.mediaResumeSecondsByChapter?.[key] : undefined;
  if (typeof seconds !== "number" || seconds <= 0) {
    media.resumeApplied = true;
    return;
  }
	const apply = () => {
    const duration = playbackDurationSeconds(media);
    if (duration <= 0) return;
		// A cursor at the completion threshold is a finished chapter: every
		// entry restarts it from 0:00. Resuming the endpoint used to replay the
		// final seconds and instantly auto-advance away (prev-chapter trap).
		if (seconds >= duration * MEDIA_COMPLETED_RATIO) {
			if (key) {
				const runtime = runtimeOf(root);
				if (runtime) runtime.mediaResumeSecondsByChapter = { ...(runtime.mediaResumeSecondsByChapter ?? {}), [key]: 0 };
			}
			setPlaybackCurrentSeconds(media, 0);
			media.resumeApplied = true;
			syncMediaUi(root, media);
			return;
		}
		const clampedSeconds = Math.min(duration, Math.max(0, seconds));
		setPlaybackCurrentSeconds(media, clampedSeconds);
		if (key) {
			const runtime = runtimeOf(root);
			if (runtime) runtime.mediaResumeSecondsByChapter = { ...(runtime.mediaResumeSecondsByChapter ?? {}), [key]: clampedSeconds };
		}
		media.resumeApplied = true;
		syncMediaUi(root, media);
		// A duration correction (especially a short rendition) is a real cursor
		// update, not merely a local display clamp. Persist it immediately.
		persistMediaProgress(root, media, true);
  };
  if (playbackDurationSeconds(media) > 0) apply();
  else media.element.addEventListener("loadedmetadata", apply, { once: true });
}

export function formatTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

function formatRate(rate: number): string {
  return rate.toFixed(rate % 1 === 0 ? 0 : 2).replace(/0$/, "").replace(/\.$/, "");
}

function completedVideoSet(root: Root): Set<string> {
  const holder = root as unknown as { __completedVideoChapters?: Set<string> };
  if (!holder.__completedVideoChapters) holder.__completedVideoChapters = new Set<string>();
  return holder.__completedVideoChapters;
}

export function markCompletedVideoChapter(root: Root, key: string): void {
  completedVideoSet(root).add(key);
}

export function clearCompletedVideoChapter(root: Root, key: string): void {
  completedVideoSet(root).delete(key);
}

function isCompletedVideoChapter(root: Root, key: string): boolean { return completedVideoSet(root).has(key); }

async function resolveChapterId(
  rt: MediaRuntimeRef & { api?: PrototypeApiClient },
  topicId: string,
  chapterIndex: number,
): Promise<string | null> {
  const cached = rt.chapterIdByIndex?.[topicId]?.[chapterIndex];
  if (cached) return cached;
  if (!rt.api) return null;
  try {
    const list = await rt.api.listTopicChapters(topicId);
    const byIndex = Object.fromEntries((list.chapters ?? []).map((chapter) => [chapter.index, chapter.id]));
    rt.chapterIdByIndex = {
      ...(rt.chapterIdByIndex ?? {}),
      [topicId]: { ...(rt.chapterIdByIndex?.[topicId] ?? {}), ...byIndex },
    };
    return rt.chapterIdByIndex[topicId]?.[chapterIndex] ?? null;
  } catch {
    return null;
  }
}
